import { gql, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { FaqList } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { SupporterFeatureComparison } from '@web/src/features/supporter/supporter-feature-comparison'
import { SupporterHero } from '@web/src/features/supporter/supporter-hero'
import {
  faqItems,
  membershipFeatures,
  type SupporterMonthsOption,
  supporterPricing,
} from '@web/src/features/supporter/supporter-page-content'
import { SupporterPaymentNotice } from '@web/src/features/supporter/supporter-payment-notice'
import { SupporterSupportOptionsCard } from '@web/src/features/supporter/supporter-support-options-card'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { CREATE_PAYMENT_REQUEST } from '@web/src/lib/mutations'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { SupporterPageQuery, SupporterPageQueryVariables } from 'types/graphql'

const DAYS_PER_MONTH = 30

function calculateTotalCents(months: SupporterMonthsOption, totalPeopleCount: number): number {
  if (totalPeopleCount < 1) return 0
  const extraPartnersBeyondFirst = Math.max(0, totalPeopleCount - 2)
  return (
    supporterPricing.durationTotalsCents[months] +
    months * extraPartnersBeyondFirst * supporterPricing.extraPartnerMonthlyCents
  )
}

function monthsToDays(months: SupporterMonthsOption): number {
  return months * DAYS_PER_MONTH
}

const SUPPORTER_PAGE_QUERY: TypedDocumentNode<SupporterPageQuery, SupporterPageQueryVariables> = gql`
  query SupporterPageQuery {
    currentProfile {
      id
      uniqueId
      relationship
      linkedPartners {
        id
        nickName
        profileId
        profile {
          id
          name
          isVerified
          lastOnline
          supporter
          profileMedia {
            reviewStatus
            imageUrlSmall
            nsfwRating
          }
        }
      }
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/supporter/')({
  component: MembershipPage,
  // The query causes the current page to reload twice so we don't preload this route.
  preload: false,
  head: () => ({
    meta: [
      { title: `Membership Levels | ${env.VITE_APP_NAME}` },
      { name: 'description', content: 'Compare free and supporter memberships' },
      { name: 'robots', content: 'nofollow' },
    ],
  }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, SUPPORTER_PAGE_QUERY)
    return { queryRef }
  },
})

function MembershipPage() {
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<SupporterPageQuery>)
  const partners = data?.currentProfile?.linkedPartners ?? []
  const relationship = data?.currentProfile?.relationship
  const [months, setMonths] = useState<SupporterMonthsOption>(3)
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<Set<string>>(new Set())
  const [expandedBenefits, setExpandedBenefits] = useState<Set<string>>(new Set())
  const hasAppliedInitialPartnerSelection = useRef(false)

  useEffect(() => {
    if (relationship === 'SINGLE') {
      hasAppliedInitialPartnerSelection.current = false
      setSelectedPartnerIds(new Set())
      return
    }
    if (hasAppliedInitialPartnerSelection.current || partners.length === 0) return
    const firstId = partners[0]?.id
    if (!firstId) return
    hasAppliedInitialPartnerSelection.current = true
    setSelectedPartnerIds(new Set([firstId]))
  }, [partners, relationship])

  const [createPaymentRequestMutation, { loading: submitting }] = useMutation(CREATE_PAYMENT_REQUEST, {
    onCompleted: () => {
      navigate({
        href: `/supporter/e-transfer-instructions?amount=${encodeURIComponent(totalDollars)}`,
      })
    },
    onError: (err) => console.error(err),
  })

  const togglePartner = (partnerId: string) => {
    setSelectedPartnerIds((prev) => {
      const next = new Set(prev)
      if (next.has(partnerId)) {
        next.delete(partnerId)
      } else {
        next.add(partnerId)
      }
      return next
    })
  }

  const totalCents = useMemo(
    () => calculateTotalCents(months, 1 + selectedPartnerIds.size),
    [months, selectedPartnerIds.size]
  )
  const totalDollars = (totalCents / 100).toFixed(2)

  const selectedPartnersInOrder = useMemo(
    () => partners.filter((p) => selectedPartnerIds.has(p.id)),
    [partners, selectedPartnerIds]
  )

  const priceBreakdown = useMemo(() => {
    const lines: { label: string; cents: number }[] = []
    lines.push({ label: 'Your support', cents: supporterPricing.durationTotalsCents[months] })
    selectedPartnersInOrder.forEach((partner, index) => {
      const cents = index === 0 ? 0 : supporterPricing.extraPartnerMonthlyCents * months
      lines.push({ label: `Partner - ${partner.nickName ? `${partner.nickName}` : ''}`, cents })
    })
    return lines
  }, [months, selectedPartnersInOrder])

  const peopleCount = 1 + selectedPartnerIds.size
  const durationTotalsCents = useMemo(
    () =>
      ({
        3: calculateTotalCents(3, peopleCount),
        6: calculateTotalCents(6, peopleCount),
        12: calculateTotalCents(12, peopleCount),
      }) as Record<SupporterMonthsOption, number>,
    [peopleCount]
  )

  const handleMakePayment = async () => {
    if (totalCents <= 0) return
    await createPaymentRequestMutation({
      variables: {
        input: {
          days: monthsToDays(months),
          partnerIds: Array.from(selectedPartnerIds),
          total: totalCents / 100,
        },
      },
    })
  }

  const toggleBenefitDetails = (key: string) => {
    setExpandedBenefits((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col items-stretch gap-8 px-4 py-4 md:gap-6 md:px-6 md:py-8">
      <SupporterHero />
      <SupporterPaymentNotice />
      {/* <SupporterPricingIntro /> */}
      {/* <SupporterTierOverview features={membershipFeatures} /> */}
      <SupporterSupportOptionsCard
        durationTotalsCents={durationTotalsCents}
        months={months}
        onMakePayment={handleMakePayment}
        onMonthsChange={setMonths}
        onTogglePartner={togglePartner}
        partners={partners}
        priceBreakdown={priceBreakdown}
        profileId={data?.currentProfile?.id}
        relationship={relationship}
        selectedPartnerIds={selectedPartnerIds}
        submitting={submitting}
        totalDollars={totalDollars}
      />
      <SupporterFeatureComparison
        expandedBenefits={expandedBenefits}
        features={membershipFeatures}
        onToggleDetails={toggleBenefitDetails}
      />
      <div className="pt-8">
        <FaqList items={[...faqItems]} />
      </div>
    </div>
  )
}
